"use client"

import { useCallback, useEffect } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { Button, Modal } from "@tc/ui-dfe/components"
import { FormDatePicker } from "@tc/ui-dfe/components/Form/FormDatePicker"
import { FormSearch } from "@tc/ui-dfe/components/Config/Form/FormSearch"
import { InfoIcon } from "@tc/ui-dfe/icons"
import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Dayjs } from "@tc/ui-dfe/utils/libs/dayjs"
import { StatusReportType } from "@tc/ui-dfe/query"
import { STATUS_REPORT_CONFIG } from "@tc/ui-dfe/utils/constants"
import { collectionAlias, CollectionName } from "@tc/search-document-builders"

const statusReportDownloadSchema = z
  .object({
    organisationIds: z
      .array(
        z.object({
          id: z.string(),
          name: z.string().optional(),
          registrationId: z.string().optional(),
        }),
      )
      .optional(),
    dateRangeStart: z.custom<Dayjs>(
      (val) => val !== null && val !== undefined,
      {
        message: "Start date is required",
      },
    ),
    dateRangeEnd: z.custom<Dayjs>((val) => val !== null && val !== undefined, {
      message: "End date is required",
    }),
  })
  .refine(
    (data) => {
      if (data.dateRangeStart && data.dateRangeEnd) {
        return (
          data.dateRangeStart.isBefore(data.dateRangeEnd) ||
          data.dateRangeStart.isSame(data.dateRangeEnd, "day")
        )
      }
      return true
    },
    {
      message: "Start date must be before or equal to end date",
      path: ["dateRangeEnd"],
    },
  )

type StatusReportDownloadFormValues = z.infer<typeof statusReportDownloadSchema>

export interface StatusReportDownloadModalProps {
  isOpen: boolean
  onClose: () => void
  onDownload: (
    organisationIds: string[] | undefined,
    dateRangeStart: string,
    dateRangeEnd: string,
  ) => void
  reportType: StatusReportType
  context: "exports" | "transfers"
}

// Custom option formatter to show registration ID and name only
const formatRegistrationOption = (item: Record<string, unknown>): string => {
  const id = (item?.registration_name as string) || ""
  const name = (item?.organisation_name as string) || ""
  return `${id} - ${name}`
}

export const StatusReportDownloadModal = ({
  isOpen,
  onClose,
  onDownload,
  reportType,
  context,
}: StatusReportDownloadModalProps) => {
  const config = STATUS_REPORT_CONFIG[reportType]
  const REGISTRATION_COLLECTION = collectionAlias(CollectionName.Registrations)

  const methods = useForm<StatusReportDownloadFormValues>({
    resolver: zodResolver(statusReportDownloadSchema),
    defaultValues: {
      organisationIds: [],
      dateRangeStart: undefined,
      dateRangeEnd: undefined,
    },
    mode: "onChange",
  })

  const {
    handleSubmit,
    reset,
    control,
    formState: { isValid },
  } = methods

  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  const handleClose = useCallback(
    (_event?: object, reason?: string) => {
      if (reason === "backdropClick") return
      onClose()
      reset()
    },
    [onClose, reset],
  )

  const submitHandler = handleSubmit((data: StatusReportDownloadFormValues) => {
    // Extract registration IDs from selected organisations
    const organisationIds =
      data.organisationIds && data.organisationIds.length > 0
        ? data.organisationIds.map((org) => org.registrationId ?? org.id)
        : undefined

    onDownload(
      organisationIds,
      data.dateRangeStart.format("YYYY-MM-DD"),
      data.dateRangeEnd.format("YYYY-MM-DD"),
    )
  })

  return (
    <Modal
      open={isOpen}
      header={config.title}
      fullWidth={false}
      disableFullScreenButton={true}
      maxWidth="sm"
      onSubmit={submitHandler}
      onClose={handleClose}
      actions={
        <Stack spacing={1} direction="row">
          <Button variant="text" ariaLabel="Cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            ariaLabel="Download"
            type="submit"
            disabled={!isValid}
          >
            Download
          </Button>
        </Stack>
      }
    >
      <FormProvider {...methods}>
        <Stack spacing={3} sx={{ minWidth: { sm: 400 } }}>
          <Stack spacing={1}>
            <Typography variant="body1" fontWeight={600}>
              {config.organisationLabel}
            </Typography>
            <FormSearch
              id="organisationIds"
              name="organisationIds"
              control={control}
              collectionName={REGISTRATION_COLLECTION}
              queryAttributes="registration_name,organisation_name"
              displayAttr="organisation_name"
              valueAttribute="registration_name"
              placeholder="Search and select"
              orderBy="registration_name:asc"
              customOption={formatRegistrationOption}
              usePermissions
              context={context}
              multiple
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body1" fontWeight={600}>
              {config.dateLabel}
              <Typography component="span" color="error.main">
                *
              </Typography>
            </Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <FormDatePicker name="dateRangeStart" control={control} />
              </Box>
              <Typography sx={{ pt: 1 }}>To</Typography>
              <Box sx={{ flex: 1 }}>
                <FormDatePicker name="dateRangeEnd" control={control} />
              </Box>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              backgroundColor: "grey.100",
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              This will download the report based on the above filters only.
            </Typography>
          </Stack>
        </Stack>
      </FormProvider>
    </Modal>
  )
}
